<template>
  <div>
    <sr-nav-dialog
      v-model="visible"
      :nav-items="navItems"
      :current-key.sync="tab"
      header="My Profile"
      :subtitle="lastUpdatedSubtitle"
      :hide-primary="hidePrimary"
      primary-text="Save"
      primary-cy="personal-info-save-btn"
      :primary-disabled="primaryDisabled"
      :primary-loading="primaryLoading"
      @primaryClick="triggerSave"
      @close="onClose"
    >
      <template #content="{ item }">
        <div v-if="item.key === 0">
          <my-profile-header
            v-if="user"
            :user="user"
            @update-profile-picture="updateProfilePicture"
          />
          <sr-user-info
            ref="userInfo"
            :edit="true"
            :user="user"
            :role-environments="roleEnvironments"
            :user-guid="userGuid"
            :company-guid="companyGuid"
            @change="handleUserInfoChange"
            @changed="setHasUserInfoChanges"
            @save="handleUserInfoSave"
            @save-disabled="setSaveDisabled"
            @saving="setSaving"
          />
        </div>
        <div v-else-if="item.key === 1">
          <permissions
            ref="permissions"
            :user="user"
            :company-guid="companyGuid"
            :view-only="true"
            flush
          />
        </div>
        <div v-else-if="item.key === 2 && isEditingSelf">
          <change-password
            :key="passwordKey"
            ref="changePassword"
            :initiator-user-guid="initiatorUserGuid"
            @change="handlePasswordChange"
            @save-disabled="setPasswordSaveDisabled"
          />
        </div>
      </template>
    </sr-nav-dialog>

    <discard-changes-modal
      :toggle-modal="toggleDiscardChangesModalVisible"
      @close="handleConfirmDiscardChanges"
      @cancel="toggleDiscardChangesModal"
    />
  </div>
</template>

<script>
import srUserInfo from '../srui/srUserInfo'
import Permissions from '../permissions'
import changePassword from '../srui/changePassword.vue'
import myProfileHeader from '../srui/myProfileHeader'
import discardChangesModal from '../srui/discardChangesModal'
import srNavDialog from '../srui/srNavDialog.vue'

import {
  user,
  rolesPermissions,
  updateUserProfilePicture
} from '../graphql/signing_room_queries.gql'
import { mapGetters, mapMutations } from 'vuex'
import { isEmpty } from 'lodash'

export default {
  name: 'MyProfileNavDialog',

  components: {
    srUserInfo,
    changePassword,
    Permissions,
    myProfileHeader,
    discardChangesModal,
    srNavDialog
  },
  props: {
    userGuid: {
      type: String,
      default: '',
      required: true
    },
    companyGuid: {
      type: String,
      required: false,
      default: null
    },
    value: {
      type: Boolean,
      default: false,
      required: false
    },
    initialTab: {
      type: Number,
      default: 0,
      required: false
    }
  },

  data () {
    return {
      visible: this.value,
      tab: this.initialTab,
      user: null,
      lastLoginAt: null,
      profilePictureUrl: '',
      roleEnvironments: {},
      hasUserInfoChanges: false,
      hasPasswordChanges: false,
      saveDisabled: true,
      saving: false,
      passwordSaveDisabled: true,
      toggleDiscardChangesModalVisible: false,
      passwordKey: 100
    }
  },

  computed: {
    ...mapGetters({
      loggedInUser: 'user',
      orgId: 'orgId'
    }),
    hasChanges () {
      return this.hasUserInfoChanges || this.hasPasswordChanges
    },
    // "Last updated" covers the whole user record, not just the picture it used
    // to sit beside. The dialog renders its subtitle under whichever panel is
    // active, so scope it to User Info rather than showing it over Permissions
    // or the password form.
    lastUpdatedSubtitle () {
      if (this.tab !== 0 || !this.user || !this.user.formattedUpdatedAt) {
        return ''
      }
      return `Last updated ${this.user.formattedUpdatedAt}`
    },
    // Permissions is view-only; User Info and Change Password both save through
    // the dialog's footer button, so it reflects whichever tab is active.
    hidePrimary () {
      return this.tab === 1
    },
    primaryDisabled () {
      return this.tab === 2 ? this.passwordSaveDisabled : this.saveDisabled
    },
    primaryLoading () {
      // changePassword has no in-flight state to report.
      return this.tab === 2 ? false : this.saving
    },
    isEditingSelf () {
      return this.loggedInUser.guid === this.userGuid
    },
    initiatorUserGuid () {
      return this.loggedInUser?.guid
    },
    navItems () {
      const iconMap = { 'User Info': 'person', Permissions: 'document-check', 'Change Password': 'lock' }
      return this.tabs.map(tab => ({ key: tab.key, label: tab.name, title: tab.name, baseName: tab.name, icon: iconMap[tab.name] || null }))
    },
    tabs () {
      const tabs = [
        { key: 0, name: 'User Info', baseName: null, icon: null },
        { key: 1, name: 'Permissions', baseName: 'Permissions', icon: 'BriefCase' }
      ]
      if (this.isEditingSelf) {
        tabs.push({ key: 2, name: 'Change Password', baseName: 'Change Password', icon: 'BriefCase' })
      }
      return tabs
    }
  },

  watch: {
    value (val) {
      this.visible = val
    },
    initialTab (val) {
      this.tab = val
    },
    userGuid () {
      if (this.userGuid) {
        this.getUser()
      }
    },
    toggleDiscardChangesModalVisible () { this.resetAppTimeout() }
  },
  methods: {
    ...mapMutations(['setSuccessToast', 'setErrorToastType', 'setUser', 'resetAppTimeout']),
    handleUserInfoChange (userInfoChanges) {
      if (!isEmpty(userInfoChanges)) {
        this.hasUserInfoChanges = true
      } else {
        this.hasUserInfoChanges = false
      }
      this.resetAppTimeout()
    },
    setHasUserInfoChanges (val) {
      this.hasUserInfoChanges = val
    },
    setSaveDisabled (val) {
      this.saveDisabled = val
    },
    setSaving (val) {
      this.saving = val
    },
    setPasswordSaveDisabled (val) {
      this.passwordSaveDisabled = val
    },
    triggerSave () {
      if (this.tab === 2) {
        if (this.$refs.changePassword) {
          this.$refs.changePassword.handleSave()
        }
        return
      }
      if (this.$refs.userInfo) {
        this.$refs.userInfo.handleSave()
      }
    },
    handlePasswordChange (hasChanges) {
      this.hasPasswordChanges = hasChanges
    },
    toggleDiscardChangesModal () {
      this.toggleDiscardChangesModalVisible = !this.toggleDiscardChangesModalVisible
    },
    handleConfirmDiscardChanges () {
      this.toggleDiscardChangesModalVisible = false
      this.tab = 0
      this.$emit('input', false)
      this.getUser()
      this.passwordKey++
    },
    onClose () {
      if (this.hasChanges) {
        this.toggleDiscardChangesModal()
      } else {
        this.tab = 0
        this.$emit('input', false)
      }
    },
    async getUser () {
      await this.$apollo.query({
        query: user,
        fetchPolicy: 'no-cache',
        variables: {
          guid: this.userGuid
        }
      }).then(({ data: { user } }) => {
        if (user) {
          this.user = user
          this.profilePictureUrl = user.profilePictureUrl
          this.lastLoginAt = user.lastLoginAt
          this.getRoleEnvironments()
        }
      })
    },
    getRoleEnvironments () {
      const companyGuid = this.companyGuid || this.user?.company?.guid
      if (!companyGuid) {
        return
      }
      this.$apollo.query({
        query: rolesPermissions,
        variables: {
          companyGuid
        },
        fetchPolicy: 'no-cache'
      }).then(({ data }) => {
        const { nodes } = data.roles
        const envMap = {}
        nodes.map(node => {
          const flatPermissions = JSON.parse(node.flatPermissions)
          if (flatPermissions.permission_setting_staging_enabled) {
            if (flatPermissions.permission_setting_production_enabled) {
              envMap[node.name] = ['Production', 'Staging']
            } else {
              envMap[node.name] = ['Staging']
            }
          } else if (flatPermissions.permission_setting_production_enabled) {
            envMap[node.name] = ['Production']
          }
        })
        this.roleEnvironments = envMap
      }).catch((err) => {
        this.setErrorToastType({ description: `There was an error with the request: ${err}` })
      })
    },
    async updateProfilePicture (image, filename) {
      if (!image || !filename) {
        this.setErrorToastType({ description: 'No image available to upload' })
        return
      }

      const { data } = await this.$apollo.mutate({
        mutation: updateUserProfilePicture,
        fetchPolicy: 'no-cache',
        variables: {
          guid: this.userGuid,
          image: image,
          filename: filename
        }
      })

      const { updateUserProfilePicture: { response, error } } = data
      if (response) {
        // this fetch ensures the photo is updated in the UI when the modal closes.
        const streamUrl = response
        await fetch(streamUrl).then(res => res.blob()).then(blob => {
          this.profilePictureUrl = URL.createObjectURL(blob)
        })

        this.setSuccessToast({ description: 'Profile picture has been saved' })
      } else if (error) {
        this.setErrorToastType({ description: `Failed to update profile picture: ${error}` })
      }

      if (this.userGuid === this.loggedInUser.guid && response) {
        this.setUser({ ...this.loggedInUser, profilePictureUrl: response })
      }

      this.getUser() // get updated information
    },
    handleUserInfoSave () {
      this.$emit('change')
      this.$emit('input', false)
      if (!this.isEditingSelf) {
        this.getUser()
      }
    }
  }
}
</script>

<style scoped>
</style>
